/* ============================================
   LeadActividad — qué le fue pasando a esta oportunidad

   Es historia: se consulta, no se opera. Por eso va al pie de la barra
   lateral y no en el cuerpo de la ficha, donde empujaba hacia abajo todo lo
   que sí se usa.

   No se consulta nada: todo sale del lead que ya está cargado y de las
   reuniones que la ficha ya pidió.
   ============================================ */
import { useMemo, type ReactNode } from 'react'
import type { Lead, FormularioItem } from '@/lib/types'
import {
  IconPlus, IconFileText, IconCheckCircle, IconCalendar, IconClock,
} from '@/components/icons/Icons'

interface Agendamiento {
  id: number
  fecha: string
  hora_inicio: string
  hora_fin: string
  google_meet_url?: string | null
  created_at: string
}

interface LeadActividadProps {
  lead: Lead
  meetings: Agendamiento[]
  etapaLabel: string
}

function fechaCorta(iso: string | null): string {
  if (!iso) return '—'
  const d = iso.length <= 10 ? new Date(iso + 'T12:00:00') : new Date(iso)
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface Evento {
  id: string
  icon: ReactNode
  titulo: string
  detalle?: string
  fecha: string
  color: string
  orden: number
}

export default function LeadActividad({ lead, meetings, etapaLabel }: LeadActividadProps) {
  const actividad = useMemo(() => {
    const formData: FormularioItem[] = Array.isArray(lead.formulario_datos) ? lead.formulario_datos : []
    const eventos: Evento[] = []
    const creado = lead.created_at ? new Date(lead.created_at).getTime() : 0
    const actualizado = lead.updated_at ? new Date(lead.updated_at).getTime() : 0

    eventos.push({
      id: 'creado',
      icon: <IconPlus size={14} />,
      titulo: 'Lead creado',
      detalle: `Código: ${lead.codigo} · Origen: ${lead.origen}`,
      fecha: fechaCorta(lead.created_at),
      color: 'var(--primary-light)',
      orden: creado,
    })

    if (formData.length > 0) {
      eventos.push({
        id: 'formulario',
        icon: <IconFileText size={14} />,
        titulo: 'Formulario completado',
        detalle: formData.map(f => `${f.pregunta}: ${Array.isArray(f.respuesta) ? f.respuesta.join(', ') : f.respuesta}`).join(' · '),
        fecha: fechaCorta(lead.created_at),
        color: 'var(--info)',
        orden: creado + 1,
      })
    }

    if (lead.etapa !== 'lead') {
      eventos.push({
        id: 'etapa',
        icon: <IconCheckCircle size={14} />,
        titulo: `Etapa: ${etapaLabel}`,
        fecha: fechaCorta(lead.updated_at),
        color: 'var(--success)',
        orden: actualizado,
      })
    }

    meetings.forEach(m => {
      eventos.push({
        id: `reunion-${m.id}`,
        icon: <IconCalendar size={14} />,
        titulo: `Reunión: ${fechaCorta(m.fecha)} ${m.hora_inicio}–${m.hora_fin}`,
        detalle: m.google_meet_url ? 'Con Google Meet' : undefined,
        fecha: fechaCorta(m.created_at),
        color: 'var(--warning)',
        orden: new Date(m.created_at).getTime(),
      })
    })

    return eventos.sort((a, b) => b.orden - a.orden)
  }, [lead, meetings, etapaLabel])

  return (
    <section className="ld-panel">
      <div className="ld-panel__header">
        <h4 className="ld-panel__titulo"><IconClock size={14} /> Actividad</h4>
      </div>

      {actividad.length === 0 ? (
        <p className="ld-panel__vacio-texto">Sin actividad registrada</p>
      ) : (
        <div className="ld-timeline ld-timeline--lateral">
          {actividad.map(ev => (
            <div key={ev.id} className="ld-timeline-event">
              <div className="ld-timeline-dot" style={{ background: ev.color }} />
              <div className="ld-timeline-body">
                <div className="ld-timeline-header">
                  <span className="ld-timeline-icon" style={{ color: ev.color }}>{ev.icon}</span>
                  <span className="ld-timeline-title">{ev.titulo}</span>
                  <span className="ld-timeline-time">{ev.fecha}</span>
                </div>
                {ev.detalle && <p className="ld-timeline-desc">{ev.detalle}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
